import React, { useEffect, createElement } from 'react';
interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  path?: string; // e.g. '/' or '/privacy'
  noindex?: boolean;
}
const SITE_URL = 'https://2opinion-online.ru';
const SITE_NAME = '2opinion — Независимый консилиум';
const OG_IMAGE = `${SITE_URL}/og-image.png`;
function setMeta(selector: string, attrs: Record<string, string>): void {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement('meta');
    Object.entries(attrs).forEach(([k, v]) => {
      if (k !== 'content') el!.setAttribute(k, v);
    });
    document.head.appendChild(el);
  }
  el.setAttribute('content', attrs.content);
}
function setLink(rel: string, href: string): void {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}
function setJsonLd(id: string, data: object): void {
  let el = document.head.querySelector<HTMLScriptElement>(`script[data-seo="${id}"]`);
  if (!el) {
    el = document.createElement('script');
    el.setAttribute('type', 'application/ld+json');
    el.setAttribute('data-seo', id);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}
export const SEO = ({
  title,
  description,
  keywords,
  path = '/',
  noindex = false
}: SEOProps) => {
  useEffect(() => {
    const fullUrl = `${SITE_URL}${path}`;
    // <html lang>
    document.documentElement.setAttribute('lang', 'ru');
    // <title>
    document.title = title;
    // Basic meta
    setMeta('meta[name="description"]', {
      name: 'description',
      content: description
    });
    if (keywords) {
      setMeta('meta[name="keywords"]', {
        name: 'keywords',
        content: keywords
      });
    }
    setMeta('meta[name="robots"]', {
      name: 'robots',
      content: noindex ? 'noindex,nofollow' : 'index,follow'
    });
    setMeta('meta[name="viewport"]', {
      name: 'viewport',
      content: 'width=device-width, initial-scale=1, viewport-fit=cover'
    });
    setMeta('meta[name="theme-color"]', {
      name: 'theme-color',
      content: '#7C9A8B'
    });
    setMeta('meta[name="format-detection"]', {
      name: 'format-detection',
      content: 'telephone=no'
    });
    // Open Graph
    setMeta('meta[property="og:type"]', {
      property: 'og:type',
      content: 'website'
    });
    setMeta('meta[property="og:site_name"]', {
      property: 'og:site_name',
      content: SITE_NAME
    });
    setMeta('meta[property="og:title"]', {
      property: 'og:title',
      content: title
    });
    setMeta('meta[property="og:description"]', {
      property: 'og:description',
      content: description
    });
    setMeta('meta[property="og:url"]', {
      property: 'og:url',
      content: fullUrl
    });
    setMeta('meta[property="og:image"]', {
      property: 'og:image',
      content: OG_IMAGE
    });
    setMeta('meta[property="og:locale"]', {
      property: 'og:locale',
      content: 'ru_RU'
    });
    // Twitter
    setMeta('meta[name="twitter:card"]', {
      name: 'twitter:card',
      content: 'summary_large_image'
    });
    setMeta('meta[name="twitter:title"]', {
      name: 'twitter:title',
      content: title
    });
    setMeta('meta[name="twitter:description"]', {
      name: 'twitter:description',
      content: description
    });
    setMeta('meta[name="twitter:image"]', {
      name: 'twitter:image',
      content: OG_IMAGE
    });
    // Canonical
    setLink('canonical', fullUrl);
    // Structured data — Organization / MedicalBusiness
    setJsonLd('organization', {
      '@context': 'https://schema.org',
      '@type': 'MedicalBusiness',
      name: SITE_NAME,
      url: SITE_URL,
      email: 'info@2opinion-online.ru',
      description: 'Независимая экспертная оценка медицинской документации по позвоночнику. Два независимых мнения от ТОП-20 врачей Москвы за 48 часов.',
      areaServed: {
        '@type': 'Country',
        name: 'Россия'
      },
      medicalSpecialty: ['Neurology', 'Orthopedic'],
      founder: {
        '@type': 'Person',
        name: 'Федин Андрей Дмитриевич'
      }
    });
    // Structured data — Service
    if (path === '/') {
      setJsonLd('service', {
        '@context': 'https://schema.org',
        '@type': 'Service',
        serviceType: 'Второе медицинское мнение по позвоночнику',
        provider: {
          '@type': 'MedicalBusiness',
          name: SITE_NAME,
          url: SITE_URL
        },
        areaServed: {
          '@type': 'Country',
          name: 'Россия'
        },
        description: 'Независимая экспертная оценка медицинской документации: два мнения за 48 часов от ТОП-20 врачей Москвы по позвоночнику.',
        offers: {
          '@type': 'Offer',
          priceCurrency: 'RUB',
          availability: 'https://schema.org/InStock'
        }
      });
    }
  }, [title, description, keywords, path, noindex]);
  return null;
};